<template>
    <Teleport to="body">
        <Transition name="mq-fade">
            <div v-if="isOpen" class="mq-overlay">
                <div class="mq-backdrop" @click="close"></div>

                <div class="mq-dialog" role="dialog" aria-modal="true">

                    <!-- Header -->
                    <div class="mq-header">
                        <div class="mq-header-icon">
                            <i class="ri-drag-move-2-line"></i>
                        </div>
                        <div class="mq-header-text">
                            <div class="mq-header-title">Mover para Quadro</div>
                            <div class="mq-header-sub">Altere o quadro do Kanban deste atendimento</div>
                        </div>
                        <button class="mq-close-btn" @click="close" title="Fechar">
                            <i class="ri-close-line"></i>
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="mq-body">
                        <div class="mq-current">
                            <span class="mq-current-label">Quadro atual</span>
                            <span class="mq-current-value">
                                <i class="ri-layout-column-line me-1"></i>{{ currentQuadroNome }}
                            </span>
                        </div>

                        <SelectField
                            label="Mover para"
                            icon="ri-arrow-right-line"
                            placeHolder="Selecione o quadro de destino"
                            :options="opcoes"
                            labelOption="nome"
                            valueProp="id"
                            :canClear="false"
                            v-model="selectedId"
                        />

                        <label v-if="quadroDestinoTemMensagem" class="mq-check">
                            <input type="checkbox" v-model="enviarMensagem" />
                            <span>Enviar mensagem de movimentação ao contato</span>
                        </label>

                        <p v-if="!opcoes.length" class="mq-empty">
                            Nenhum outro quadro disponível para mover.
                        </p>
                    </div>

                    <!-- Footer -->
                    <div class="mq-footer">
                        <button type="button" class="mq-btn mq-btn--ghost" @click="close">Cancelar</button>
                        <button
                            type="button"
                            class="mq-btn mq-btn--primary"
                            :disabled="!selectedId || saving"
                            @click="confirmar"
                        >
                            <i v-if="saving" class="ri-loader-4-line mq-spin me-1"></i>
                            Mover
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import SelectField from '@/components/forms/SelectField.vue';

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    // Lista de quadros de destino disponíveis (ResponseQuadroKanban[]).
    quadros: { type: Array, default: () => [] },
    // Id do quadro atual do atendimento (ou null = Fila / Sem quadro).
    currentQuadroId: { type: String, default: null },
    // Nome do quadro atual, resolvido pelo pai (lista completa de quadros).
    currentQuadroNome: { type: String, default: 'Fila / Sem quadro' },
    saving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);

const selectedId = ref(null);
const enviarMensagem = ref(false);

// Não deixa escolher o próprio quadro atual como destino.
const opcoes = computed(() => props.quadros.filter(q => q.id !== props.currentQuadroId));

const quadroDestinoTemMensagem = computed(() => {
    const destino = props.quadros.find(q => q.id === selectedId.value);
    return !!destino && destino.tipo_mensagem_movimentacao && destino.tipo_mensagem_movimentacao !== 'NENHUMA';
});

// Reseta o estado a cada abertura.
watch(() => props.isOpen, (aberto) => {
    if (aberto) {
        selectedId.value = null;
        enviarMensagem.value = false;
    }
});

// Se o destino escolhido não tem mensagem de movimentação, força o flag em false.
watch(quadroDestinoTemMensagem, (tem) => {
    if (!tem) enviarMensagem.value = false;
});

function close() {
    if (props.saving) return;
    emit('close');
}

function confirmar() {
    if (!selectedId.value) return;
    const quadro = props.quadros.find(q => q.id === selectedId.value);
    if (!quadro) return;
    emit('confirm', { quadro, enviarMensagem: enviarMensagem.value });
}
</script>

<style scoped>
.mq-overlay { position: fixed; inset: 0; z-index: 1080; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.mq-backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.45); }
.mq-dialog {
    position: relative;
    width: 100%;
    max-width: 440px;
    background: var(--bs-white);
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
.mq-header { display: flex; align-items: center; gap: 12px; padding: 18px 20px; border-bottom: 1px solid var(--bs-border-color-translucent); }
.mq-header-icon {
    width: 40px; height: 40px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    border-radius: 10px; background: rgba(7, 94, 84, 0.1); color: #075e54; font-size: 20px;
}
.mq-header-text { flex: 1; min-width: 0; }
.mq-header-title { font-size: 16px; font-weight: 600; color: var(--bs-dark); }
.mq-header-sub { font-size: 12px; color: var(--bs-text-muted); }
.mq-close-btn { background: transparent; border: none; font-size: 22px; color: var(--bs-text-muted); line-height: 1; padding: 0; cursor: pointer; }
.mq-close-btn:hover { color: var(--bs-dark); }

.mq-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.mq-current { display: flex; flex-direction: column; gap: 4px; }
.mq-current-label { font-size: 12px; font-weight: 600; color: var(--bs-text-muted); text-transform: uppercase; letter-spacing: 0.03em; }
.mq-current-value {
    display: inline-flex; align-items: center; align-self: flex-start;
    padding: 6px 12px; border-radius: 8px;
    background: var(--bs-light); color: var(--bs-dark); font-weight: 500; font-size: 14px;
}
.mq-check { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--bs-dark); cursor: pointer; }
.mq-check input { width: 16px; height: 16px; cursor: pointer; }
.mq-empty { margin: 0; font-size: 13px; color: var(--bs-text-muted); }

.mq-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid var(--bs-border-color-translucent); }
.mq-btn { padding: 8px 18px; border-radius: 8px; font-size: 14px; font-weight: 500; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; }
.mq-btn--ghost { background: transparent; border-color: var(--bs-border-color); color: var(--bs-dark); }
.mq-btn--ghost:hover { background: var(--bs-light); }
.mq-btn--primary { background: #075e54; color: #fff; }
.mq-btn--primary:hover:not(:disabled) { background: #064b43; }
.mq-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.mq-spin { display: inline-block; animation: mq-rotate 0.8s linear infinite; }
@keyframes mq-rotate { to { transform: rotate(360deg); } }

.mq-fade-enter-active, .mq-fade-leave-active { transition: opacity 0.18s ease; }
.mq-fade-enter-from, .mq-fade-leave-to { opacity: 0; }
</style>
